<template>
  <main class="home">
    <TransactionsHistory
      v-if="currentViewState === TransactionViewState.LIST"
      @openFilters="updateViewState(TransactionViewState.FILTERS)"
      @openTransactionDetails="openTransactionDetails"
    />
    <TransactionDetails
      v-if="currentViewState === TransactionViewState.DETAILS"
      :transaction-info="selectedTransaction"
      @close-details="updateViewState(TransactionViewState.LIST)"
      @view-refund-original-debit="viewRefundOriginalDebit"
    />
    <TransactionFilters
      v-if="currentViewState === TransactionViewState.FILTERS"
      @close-filters-view="updateViewState(TransactionViewState.LIST)"
    />
  </main>
</template>

<script lang="ts" setup>
import { computed, getCurrentInstance, onMounted } from "vue";
import TransactionsHistory from "@/apps/credit-line/components/Transactions/TransactionsHistory.vue";
import { setLanguage } from "@/apps/credit-line/helpers/translations.helper";
import TransactionFilters from "@/apps/credit-line/components/Transactions/TransactionsFilters.vue";
import TransactionDetails from "@/apps/credit-line/components/Transactions/TransactionDetails.vue";
import { ITransaction } from "@/apps/credit-line/models/CreditLineDetails";
import { recorder, RECORDING_APPS } from "@/shared-services/recording.service";
import {
  getDebitById,
  populateTransactionsArray,
} from "@/apps/credit-line/helpers/transactions.helper";
import { useRoute } from "vue-router";
import { isAndroid } from "@/apps/credit-line/helpers/platformChecker.helper";
import { AndroidInterface } from "@/apps/credit-line/services/android.service";
import { useStore } from "vuex";
import { getRandomNumberBetweenRange } from "@/apps/credit-line/helpers/misc.helper";
import { AuthenticationHelper } from "@/apps/credit-line/helpers/authentication.helper";
import { useCreditLineSummary } from "@/apps/credit-line/hooks/useCreditLineSummary";
import { TransactionViewState } from "@/apps/credit-line/types/TransactionViewState";

declare const Android: AndroidInterface;

const app = getCurrentInstance()?.appContext.app;
const route = useRoute();
const store = useStore();
const { fetchSummary } = useCreditLineSummary();

const currentViewState = computed(
  () => store.state?.creditLine?.currentTransactionViewState
);
const selectedTransaction = computed(
  () => store.state?.creditLine?.selectedTransaction
);
const transactions = computed(() => store.state?.creditLine?.transactionList);

setLanguage(getCurrentInstance()?.proxy);

onMounted(() => {
  recorder(app)?.startRecording(RECORDING_APPS.CREDIT_LINE);
  recorder(app)?.updateRecordingInfo(getRandomNumberBetweenRange(1000000, 1), {
    deviceTag: AuthenticationHelper.getDeviceTag(),
  });

  if (route?.query?.displayId) {
    fetchSummary().then((summary) => {
      const transactionId = Number(route?.query?.displayId);
      const transactionsArray = populateTransactionsArray(summary);
      if (
        typeof transactionsArray !== "undefined" &&
        transactionsArray.length > 0
      ) {
        const queryTransaction = transactionsArray.find(
          (x) =>
            x.creditId === transactionId ||
            x.debitId === transactionId ||
            Number(x.paymentId) === transactionId
        );
        store.dispatch("creditLine/setCurrency", summary.currency);
        if (queryTransaction) {
          store.dispatch("creditLine/setSelectedTransaction", queryTransaction);
          store.dispatch(
            "creditLine/setCurrentTransactionViewState",
            TransactionViewState.DETAILS
          );
        }
      } else {
        store.dispatch(
          "creditLine/setCurrentTransactionViewState",
          TransactionViewState.LIST
        );
      }
      if (isAndroid()) {
        Android.finishedLoading();
      }
    });
  } else {
    // Check if we already have a view state from store, otherwise set to LIST
    if (!currentViewState.value) {
      store.dispatch(
        "creditLine/setCurrentTransactionViewState",
        TransactionViewState.LIST
      );
    }
  }
});

function updateViewState(state: TransactionViewState) {
  store.dispatch("creditLine/setCurrentTransactionViewState", state);
}

function openTransactionDetails(transaction: ITransaction) {
  store.dispatch("creditLine/setSelectedTransaction", transaction);
  store.dispatch(
    "creditLine/setCurrentTransactionViewState",
    TransactionViewState.DETAILS
  );
}

function viewRefundOriginalDebit(id: number | string) {
  const originalDebit = getDebitById(id, transactions.value);
  if (originalDebit) {
    openTransactionDetails(originalDebit);
  }
}
</script>
<style lang="scss" scoped>
.main {
  overflow-x: hidden !important;
}
</style>
